import React, {memo, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import cx from 'classnames'
import queryString from 'query-string'
import loadable from '@loadable/component'
import withHydrationOnDemand from 'react-hydration-on-demand'

/* Action */
import {push, replace} from 'connected-react-router'

import {actionCreators as seoActionCreators} from '../../../redux/actions/seoActions'
import {actionCreators as featureToggleActionCreators} from '../../../redux/actions/featureToggleActions'
import {actionCreators as analyticsActionCreators} from '../../../redux/actions/analyticsActions'
import {actionCreators as getGoogleAdUnitListingPageActionCreators} from '../../../redux/actions/googleAdUnitActions'
import {actionCreators as voltranActionCreators} from '../../../redux/actions/voltranActions'
import {actionCreators as dictionaryActionCreators} from '../../../redux/actions/dictionaryActions'
import {actionCreators as categoryActionCreators} from '../../../redux/actions/categoryActions'
import {actionCreators as filterActionCreators} from '../../../redux/actions/filterActions'
import {actionCreators as bannerActionCreators} from '../../../redux/actions/bannerActions'
import {actionCreators as cmsComponentActionCreators} from '../../../redux/actions/cmsComponentActions'
import {actionCreators as landingPageActionCreators} from '../../../redux/actions/landingPageActions'
import {actionCreators as productActionCreators} from '../../../redux/actions/productActions'
import {actionCreators as footerActionCreators} from '../../../redux/actions/footerActions'

/* Components */
import hbPageWithUserInfo from '../../../components/page/hbPageWithUserInfo'
import ProductListStructuredData from '../../../components/ProductListStructuredData/ProductListStructuredData'
import hbPageWithAnalytics from '../../../components/page/hbPageWithAnalytics'
import VoltranFragment from '../../../components/VoltranFragment/VoltranFragment'
import HbPageTitle from '../../../components/page/title/HbPageTitle'
import HbClientSideHelmet from '../../../components/Helmet/HbClientHelmet'
import HbBreadCrumbView from '../../../components/Breadcrumb/HbBreadCrumbView'
import MoriaComponents from '../../../components/MoriaComponentsView/MoriaComponentsView'
import SearchKeywordRowListView from '../../../views/search/keyword/row/list/view/SearchKeywordRowListView'
import CmsComponents from '../../../views/cmsComponent/view/CmsComponent/CmsComponent'
import ListingPreloadImages from '../shared/ListingPreloadImages'
import Feedback from '../../../partials/Feedback/Feedback'
import SimilarBrandAndSubcategory
  from '../../../views/product/mobile/similar-brand-and-subcategory/SimilarBrandAndSubcategory'

/* Core */
import LoadEventManager from '../../../core/event/LoadEventManager'

/* Utils */
import {resolvePromiseList, resolveWithSettleContext} from '../../../common/async/promiseUtils'
import {seoBreadCrumbConfigArraySelector} from '../../../redux/selectors/seoSelector'
import {hasAppliedFilterOnlyOneAuthorSelector} from '../../../redux/selectors/filterSelectors'
import {generateQueryString, removeQueryStringFromUrl} from '../../../common/url/urlUtils'
import {generateFeatureToggleMap} from '../../../modules/feature-toggle/featureToggleUtils'
import {
  generateCategoryHierarchyForWebtrekk,
  generateFiltersForWebtrekk,
  generateContentIdArrayForWebtrekk,
  generateUserInfoForWebtrekk,
  generateWebtrekkInitialData,
  generateHierarchyFromAppliedFilters
} from '../../../modules/analytics/webtrekk/webtrekkUtils'
import {getBrand, getCategory} from '../../../modules/googleAd/googleAdUtils'
import {isExistPartialFragment} from '../../../modules/voltran/voltranUtils'
import {getCookies} from '../../../common/network/apiUtils'
import {getCookiesByPrefix, jsonParse} from '../../../utils/objectUtils'
import {PAGINATION_PARAM_NAMES} from '../../../common/pagination/paginationConstants'
import {DESKTOP_LISTING_BANNER_SETTINGS, LANDING_PAGES_CATEGORIES} from '../../utils/constants'
import useQueryParams from '../../../hooks/useQueryParams'
import MoriaComponentsHelper from '../../../modules/moriaComponents/moriaComponentsHelper'
import {useEffectOnlyOnUpdate} from '../../../common/react/hook/lifecycle'
import {usePrevious} from '../../../common/react/hook/statement'
import {
  checkForCategoryRedirection,
  generateCategoryUrl,
  isCategoryApiPaginationLimitExceeded
} from '../../../modules/category/categoryUtils'
import {checkForDictionaryToCategoryRedirection} from '../../../modules/dictionary/dictionaryUtils'
import {getIsAppliedFilter, getIsAppliedFilterForServer, checkSimilarProductsWidgetVisibilty} from '../../../utils/filterUtil'
import {getBrowserCookie} from '../../../../client/common/persistence/browserCookieUtils'
import {findCategoryLandingPage} from '../../../modules/landingPage/landingPageUtils'
import {isSeoParagraphViewEnabledForListingPages} from '../../../modules/seo/seoUtils'
import {isHurlyPageByWindow} from '../../../utils/hurlyHelper'
import {useAdvertisement} from '../../../hooks/useAdvertisement'
import {shouldRequestBanner} from '../../utils/bannerUtils'
import {combineUrlAndQueryParams} from '../../../utils/combineUrlAndQueryParams'
import appConfig from '../../../appConfig'

/* Constants */
import {FEATURE_TOGGLE_NAMES} from '../../../modules/feature-toggle/featureToggleConstants'
import {MORIA_COMPONENTS, MORIA_PASS_COOKIE_PREFIXES} from '../../../modules/moriaComponents/moriaComponentsConstants'
import {DAVINCI_PAGE_TYPES, DAVINCI_PAGES} from '../../../modules/analytics/davinci/davinciConstants'
import {PAGE_TYPES} from '../../../modules/seo/seoConstants'
import {PAGE_TITLE_SIZES} from '../../../components/page/title/HbPageTitleConstants'
import {FACET_QUERY_PARAM_NAMES, FACET_IDS} from '../../../views/filter/facet/facetConstants'
import {DESKTOP_COMPONENTS, DESKTOP_COMPONENTS_OPTIONS} from '../../../modules/voltran/voltranConstants'
import {DICTIONARY_DETAIL_PARAMS} from '../../../modules/dictionary/dictionaryConstants'
import {BREADCRUMB_SIZES} from '../../../components/Breadcrumb/HbBreadbrumbConstants'
import {HTTP_STATUS_CODES} from '../../../common/network/httpConstants'
import {QUERY_STRING_PREFIX} from '../../../common/url/urlConstants'
import {
  CATEGORY_SEARCH_KEYWORD_TITLE,
  CATEGORY_PAGE_ANDROID_TARGET_BRANDS
} from '../../../modules/category/categoryConstants'
import {LANDING_PAGE_ENVIRONMENTS} from '../../../modules/landingPage/landingPageConstants'
import {DESKTOP_CATEGORY_PAGE_FEATURE_TOGGLES} from '../utils/featureToggles'
import {FILTER_DELIMITER} from '../../../modules/filter/filterConstants'
import {X_VARNISH_CACHE_CONTROL_KEY} from '../../../common/network/networkConstants'

/* Lazy Components */
const DictionaryList = loadable(() => import('../../../views/dictionary/view/list/DictionaryList'))
const DictionaryDetail = loadable(() => import('../../../views/dictionary/view/detail/DictionaryDetail'))
const ComparedProducts = loadable(() => import('../../../components/ComparedProducts/ComparedProducts'))

/* withHydrationOnDemand  */
const ComparedProductsWithHydrationOnDemand = withHydrationOnDemand({on: ['visible']})(ComparedProducts)

/* Styles */
import s from './category-page.scss'

const UTAG_DATA_EVENT_NAME = 'storefront.voltran.analytics.utagData'
const UTAG_DATA_OVERRIDE_EVENT_NAME = 'storefront.voltran.analytics.utagData.override'
const UTAG_DATA_RESPONSE = 'storefront.voltran.analytics.utagData.response'
const LOCATION_SEARCH_REGEX = /puan|filtreler|siralama|markalar/g

const IPHONE_CATEGORY_ID = '60005202'
const ANDROID_CATEGORY_ID = '60005201'

const CategoryPage = memo((props) => {
  const {
    appliedFacetArray,
    categories,
    categoryPageBreadCrumbConfigArray,
    dispatch,
    filteredCategory,
    fragmentsMap,
    isClosedCategory,
    location,
    pageFeatureToggleMap,
    platform,
    routeChange,
    seoInfo,
    serverSideRendering,
    userInfoPromise,
    categoryName,
    dictionaryArray,
    dictionaryDetail,
    appliedSelectedAnyDeliveryValue,
    hasAppliedFilterOnlyOneAuthor,
    categoryLandingPageInfo,
    cmsComponentsMap,
    categorySeoInfo,
    subCategories,
    isBot,
    keywords,
    isCollectionPage,
    friendlyUrl,
    url,
    comparedProducts,
  } = props
  const match = isCollectionPage ? props?.initialMatch || props?.match : props?.match
  const {
    params: {
      categoryId,
      brandIds,
      categoryUrlKeyword,
    }
  } = match
  const [showBannerState, setShowBannerState] = useState(() => {
    return !brandIds && shouldRequestBanner({locationUrl: location?.search})
  })
  const [isBannerLoading, setIsBannerLoading] = useState(false)
  const prevProps = usePrevious(props)
  const queryParams = useQueryParams()
  const isAppliedFilter = getIsAppliedFilterForServer(url)
  const [
    moriaComponents,
    addToCartFragment,
    favoriteHandlerFragment
  ] = useMemo(() => {
    const fragmentIds = [
      DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.MORIA_LISTING]?.id?.toString(),
      DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.ADD_TO_CART]?.id?.toString(),
      DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.FAVORITE]?.id?.toString(),
    ]

    return fragmentIds?.map(fragmentId => fragmentsMap?.get(fragmentId))
  }, [fragmentsMap])
  const newCategoryId = useRef(categoryId)
  const categoryLandingPageInfoRef = useRef(categoryLandingPageInfo)
  const categoryUrlSeoKeywordRef = useRef(categoryUrlKeyword)
  const {
    firstProductImages,
    productSKUs
  } = useMemo(() => MoriaComponentsHelper.getPageData(moriaComponents), [moriaComponents])

  const {
    appliedFilters: moriaAppliedFilters,
    itemListElement,
    totalProductCount
  } = useMemo(() => MoriaComponentsHelper.getSeoState(moriaComponents), [moriaComponents])

  useAdvertisement({seoInfo, pageType: DAVINCI_PAGE_TYPES.CATEGORY})

  const setLocation = (location) => {
    dispatch(
      filterActionCreators.setDeliveryLocation(location),
    )
  }

  useEffect(() => {
    const _boostrap = async() => {
      let landingPageCmsComponent = getLandingPageCmsComponent()

      LoadEventManager.handlePageRenderStart(dispatch, DAVINCI_PAGES.CATEGORY, Boolean(landingPageCmsComponent))
      LoadEventManager.handleDomLoaded(dispatch, DAVINCI_PAGES.CATEGORY, Boolean(landingPageCmsComponent))

      userInfoPromise.then(async() => {
        const locationCookie = getBrowserCookie('location')
        const locationObject = jsonParse(locationCookie)
        setLocation(locationObject)

        if (!pageFeatureToggleMap) {
          await resolveWithSettleContext(
            dispatch(
              featureToggleActionCreators.getDesktopCategoryPageFeatureToggles.trigger(
                DESKTOP_CATEGORY_PAGE_FEATURE_TOGGLES,
              ),
            ),
          )
        }
        const hasLandingPage = LANDING_PAGES_CATEGORIES?.some(category => category === categoryId)
        const isCategoryLandingPageToggleOn = pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_PWA_DESKTOP_CATEGORY_PAGES_FROM_CMS) && hasLandingPage

        if (landingPageCmsComponent) {
          sendWebtrekkData()
          LoadEventManager.handlePageRendered(dispatch)
        } else if (!landingPageCmsComponent && isCategoryLandingPageToggleOn) {
          await fetchCategoryLandingPage().then((
            [
              getCmsComponentResponse,
              getCategoryResponse,
            ]) => {
            landingPageCmsComponent = getLandingPageCmsComponent()

            if (landingPageCmsComponent) {
              dispatch(bannerActionCreators.resetBannerState())
              dispatch(categoryActionCreators.resetCategoryProducts())
              dispatch(filterActionCreators.resetFilterState())
              dispatch(filterActionCreators.closeFilterManagementPanel())

              redirectIfNecessaryForCategory(getCategoryResponse?.result?.data)
              executeAnalyticsLogic()
              LoadEventManager.handlePageRendered(dispatch)
            }
          })
        } else if (!isCategoryLandingPageToggleOn) {
          sendWebtrekkData()
        }
      })

      if (!pageFeatureToggleMap) {
        await resolveWithSettleContext(
          dispatch(
            featureToggleActionCreators.getDesktopCategoryPageFeatureToggles.trigger(
              DESKTOP_CATEGORY_PAGE_FEATURE_TOGGLES,
            ),
          )
        ).then(() => {
          fetchComponentsIfNecessary().then(() => {
            LoadEventManager.handlePageRendered(dispatch)
          })
        })
      } else {
        fetchComponentsIfNecessary().then(() => {
          LoadEventManager.handlePageRendered(dispatch)
        })
      }

      handleGetSeoInformation()
      handleUtagOverrideDataListener()
      handleCategoryDependentLogic('create')
    }

    _boostrap()

    return () => {
      window?.HbEventBus?.off(UTAG_DATA_EVENT_NAME, false)
      dispatch(seoActionCreators.getGenericSeoInfo.cancel())
      dispatch(voltranActionCreators.getFragments.cancel())
      dispatch(dictionaryActionCreators.getDictionary.cancel())
      dispatch(dictionaryActionCreators.getDictionaryDetail.cancel())
      dispatch(cmsComponentActionCreators.getCmsComponent.cancel())
    }
  }, [])

  useEffect(() => {
    if (!pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.SERVER_TO_CLIENT_SEND_INITIAL_DATA_FOR_PWA_DESKTOP_CATEGORY_PAGE)) {
      const redirection = checkForCategoryRedirection(
        {data: {categoryUrlKeyword: categoryUrlSeoKeywordRef.current}},
        categoryUrlKeyword,
        newCategoryId?.current || categoryId,
        queryParams,
        brandIds,
        landingPageCmsComponent,
      )

      redirect(redirection)
    }
  }, [categoryUrlSeoKeywordRef.current])

  useEffectOnlyOnUpdate(() => {
    handleLocationChange(prevProps)
    fetchSeo()
  }, [location])

  const executeAnalyticsLogic = () => {
    const landingPageCmsComponent = getLandingPageCmsComponent()

    sendWebtrekkData()
    LoadEventManager.handleDomLoaded(dispatch, DAVINCI_PAGES.CATEGORY, Boolean(landingPageCmsComponent))
  }

  const updateLocation = (newLocation, {isReplace = false} = {}) => {
    const landingPageCmsComponent = getLandingPageCmsComponent()

    if (isReplace) {
      dispatch(replace(newLocation))
    } else {
      dispatch(push(newLocation))
    }

    LoadEventManager.handlePageRenderStart(dispatch, DAVINCI_PAGES.CATEGORY, Boolean(landingPageCmsComponent))
  }

  const handleLocationChange = async(prevProps) => {
    const isCategoryChanged = prevProps?.match?.params?.categoryId !== categoryId
    const isAppliedFilter = getIsAppliedFilter()
    const hasLandingPage = LANDING_PAGES_CATEGORIES?.some(category => category === categoryId)
    const isCategoryLandingPageToggleOn = pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_PWA_DESKTOP_CATEGORY_PAGES_FROM_CMS) && hasLandingPage

    if (displayDictionaryContent && queryParams[DICTIONARY_DETAIL_PARAMS] && !isAppliedFilter) {
      resolveWithSettleContext(
        dispatch(
          dictionaryActionCreators.getDictionaryDetail.trigger(
            categoryId,
            queryParams[DICTIONARY_DETAIL_PARAMS],
          ),
        ),
      ).then((response) => {
        redirectIfNecessaryForDictionary(response.result.data)
      })
    }

    let landingPageCmsComponent
    if (isCategoryChanged && isCategoryLandingPageToggleOn) {
      await fetchCategoryLandingPage().then((
        [
          getCmsComponentResponse,
          getCategoryResponse,
        ]) => {
        landingPageCmsComponent = getLandingPageCmsComponent()

        if (landingPageCmsComponent) {
          dispatch(bannerActionCreators.resetBannerState())
          dispatch(categoryActionCreators.resetCategoryProducts())
          dispatch(filterActionCreators.resetFilterState())
          dispatch(filterActionCreators.closeFilterManagementPanel())

          redirectIfNecessaryForCategory(getCategoryResponse?.result?.data)
          LoadEventManager.handlePageRendered(dispatch)
          executeAnalyticsLogic()
        }
      })
    }

    if (isCategoryChanged) {
      handleCategoryDependentLogic('update')
    }
  }

  const fetchCategoryLandingPage = async() => {
    let promise = null

    await resolveWithSettleContext(
      dispatch(
        landingPageActionCreators.getCategoryLandingPageInfo.trigger(
          categoryId,
          LANDING_PAGE_ENVIRONMENTS.DESKTOP,
        ),
      ).then(getCategoryLandingPageResponse => {
        if (!getCategoryLandingPageResponse?.data?.data) {
          return getCategoryLandingPageResponse
        }

        categoryLandingPageInfoRef.current = findCategoryLandingPage(
          getCategoryLandingPageResponse.data.data,
          categoryId,
          LANDING_PAGE_ENVIRONMENTS.DESKTOP,
        )

        return getCategoryLandingPageResponse
      })
    )

    if (categoryLandingPageInfoRef?.current?.value) {
      promise = Promise.all([
        resolveWithSettleContext(
          dispatch(
            cmsComponentActionCreators.getCmsComponent.trigger([{
              name: categoryLandingPageInfoRef?.current?.value,
            }]),
          ),
        ),
        resolveWithSettleContext(
          dispatch(
            categoryActionCreators.getCategoryInformation.trigger(
              categoryId,
              {
                isDynamic: true,
              },
            ),
          ),
        ),
      ])
    }

    return promise || Promise.all([Promise.resolve()])
  }

  const redirectIfNecessaryForCategory = (categoryResponseData) => {
    const landingPageCmsComponent = getLandingPageCmsComponent()

    const redirection = checkForCategoryRedirection(
      categoryResponseData,
      categoryUrlKeyword,
      categoryId,
      queryParams,
      brandIds,
      landingPageCmsComponent,
    )

    redirect(redirection)
  }

  const redirectIfNecessaryForDictionary = (dictionaryResponseData) => {
    const redirection = checkForDictionaryToCategoryRedirection(
      dictionaryResponseData,
      categoryUrlKeyword,
      categoryId,
      queryParams,
    )

    redirect(redirection)
  }

  const redirect = (redirection) => {
    if (redirection && redirection?.url !== window.location.href) {
      if (redirection?.isInternal) {
        const [pathname, queryString] = redirection.url.split(QUERY_STRING_PREFIX)

        updateLocation({
          pathname,
          search: queryString,
          state: {
            shouldPreserveInitialFilteredCategory: true,
          },
        }, {isReplace: true})
      } else {
        window.location.replace(redirection.url)
      }
    }
  }

  const handleCategoryDependentLogic = (process) => {
    const landingPageCmsComponent = getLandingPageCmsComponent()
    const shouldLoadDictionaryOnClient = process === 'create' && !dictionaryArray && pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_DICTIONARY_CONTENT_FOR_PWA_DESKTOP_PAGES)
    const shouldLoadDictionaryOnUpdateFilter = dictionaryArray?.length === 0 || process === 'update'

    if (displayDictionaryContent && (shouldLoadDictionaryOnClient || shouldLoadDictionaryOnUpdateFilter)) {
      resolveWithSettleContext(
        dispatch(
          dictionaryActionCreators.getDictionary.trigger(
            categoryId,
          ),
        ),
      )
    }

    if (!landingPageCmsComponent) {
      fetchCategoryPageComponents()
    }
  }

  const fetchComponentsIfNecessary = async() => {
    const cmsBanner = cmsComponentsMap?.get(newCategoryId?.current || categoryId)

    await fetchCategoryPageComponents()

    if (!cmsBanner) {
      await fetchCMSComponents()
    }
  }

  function setSeoInfo(seoInfo) {
    if (seoInfo && process.env.BROWSER && window) {
      document.title = seoInfo.title
    }
  }

  const fetchCategoryPageComponents = async() => {
    let fragments = []

    if (!addToCartFragment) {
      fragments.push(DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.ADD_TO_CART])
    }

    if (!favoriteHandlerFragment) {
      fragments.push(DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.FAVORITE])
    }

    if (!moriaComponents) {
      const cookies = getCookies()
      const locationCookie = jsonParse(cookies?.location)
      const url = location?.pathname + location?.search
      const {clientUrl: moriaUrl} = MoriaComponentsHelper.getMoriaRequestUrl({
        suffix: url,
        excludedPartials: [MORIA_COMPONENTS.SEARCH_RESULT_SUMMARY, MORIA_COMPONENTS.GOOGLE_VIDEO_ADX, MORIA_COMPONENTS.JET_DELIVERY]
      })

      const rawCookies = process.env.BROWSER ? document.cookie : ''
      const moriaCookies = getCookiesByPrefix({rawCookies, prefixes: MORIA_PASS_COOKIE_PREFIXES})

      fragments.push({
        ...DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.MORIA_LISTING],
        url: moriaUrl,
        clientUrl: moriaUrl,
        paramater: {
          ...queryParams,
          ...isVerticalFilterSticky ? {} : {stickyPartials: 'VerticalFilter'},
          [FACET_QUERY_PARAM_NAMES.CITY_CODE]: locationCookie?.city?.code,
          [FACET_QUERY_PARAM_NAMES.RECEIVER_CITY]: locationCookie?.city?.name,
          [FACET_QUERY_PARAM_NAMES.RECEIVER_TOWN]: locationCookie?.town?.name,
          [FACET_QUERY_PARAM_NAMES.RECEIVER_DISTRICT]: locationCookie?.district?.name,
          isBot
        },
        headers: {
          ...moriaCookies,
          collectionpage: isCollectionPage || false,
          friendlyurl: friendlyUrl || ''
        }
      })
    }

    if (fragments?.length > 0) {
      let moriaSeoState = null
      const result = await resolveWithSettleContext(
        dispatch(voltranActionCreators.getFragments.trigger(fragments)).then(([fragmentsResponse]) => {
          moriaSeoState = MoriaComponentsHelper.getSeoState(fragmentsResponse?.result?.data) || {}
          categoryUrlSeoKeywordRef.current = moriaSeoState?.categoryInfo?.categoryUrlKeyword

          if (moriaSeoState) {
            fetchSeo({
              ...moriaSeoState
            })
          }
        }),
      )

      return result
    }

    return Promise.resolve()
  }

  const fetchCMSComponents = () => {
    const showADBanner = pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_BANNER_ENABLED_FOR_DESKTOP_CATEGORY_PAGE)

    if (showADBanner && showBannerState) {
      return resolveWithSettleContext(dispatch(
        cmsComponentActionCreators.getCmsComponent.trigger([{
          categoryId: newCategoryId?.current || categoryId,
          ...DESKTOP_LISTING_BANNER_SETTINGS
        }]),
      ))
    }

    return Promise.resolve()
  }

  const fetchSeo = (body = null) => {

    const queryParams = queryString.parse(window?.location?.search)

    if (Object.keys(queryParams)?.length <= 1 && queryParams[PAGINATION_PARAM_NAMES.PAGE]) {
      delete queryParams[PAGINATION_PARAM_NAMES.PAGE]
    }

    const isAppliedFilter = getIsAppliedFilter()
    const isDictionaryDetailPage = Boolean(queryParams[DICTIONARY_DETAIL_PARAMS]) && !isAppliedFilter
    const moriaSeoState = MoriaComponentsHelper.getSeoState(moriaComponents) || {}
    const payload = body || moriaSeoState
    const brandTopPopulerFilterMordorValues = MoriaComponentsHelper.getBrandTopPopulerValues(moriaComponents)
    const {productListMetrics = null} = MoriaComponentsHelper.getPageData(moriaComponents)

    resolveWithSettleContext(
      dispatch(
        seoActionCreators.getGenericSeoInfo.trigger({
          ...payload,
          pageType: isDictionaryDetailPage ? PAGE_TYPES.DICTIONARY : PAGE_TYPES.CATEGORY,
          keyword: queryParams[DICTIONARY_DETAIL_PARAMS],
          pageValue: match?.params?.categoryId,
          absoluteUrl: removeQueryStringFromUrl(url) || '',
          isMobile: false,
          filters: generateQueryString(queryParams),
          url: url,
          topBrands: brandTopPopulerFilterMordorValues,
          discountedProductCount: productListMetrics?.discountedProductCount,
          maxDiscountedPercentage: productListMetrics?.maxDiscountedPercentage,
          minProductPrice: productListMetrics?.minProductPrice,
          averageProductPrice: productListMetrics?.averageProductPrice,
        }),
      ),
    ).then((response) => {
      if (response?.result?.data) {
        window?.HbEventBus?.emit(UTAG_DATA_RESPONSE, {response: response.result.data.data})
        if (isDictionaryDetailPage) {
          const updateSeoData = {
            h1: moriaSeoState?.categoryInfo?.categorySeoName,
          }
          dispatch(seoActionCreators.updateSeoInfo(updateSeoData))
        }
        setSeoInfo(response.result.data.data)
      } else {
        window?.HbEventBus?.emit(UTAG_DATA_RESPONSE, {error: {success: false, message: response?.result?.message}})
      }
    }).
      catch((error) => {
        window?.HbEventBus?.emit(UTAG_DATA_RESPONSE, {error: error})
      })
  }

  const sendWebtrekkData = (userInfo) => {
    const hierarchyData = {
      breadcrumbArray: generateHierarchyFromAppliedFilters(moriaAppliedFilters)
    }

    const categoryHierarchy = generateContentIdArrayForWebtrekk(hierarchyData, 'Storefront')
    const webtrekkData = generateWebtrekkInitialData(categoryHierarchy.join('-'), 'Storefront')

    generateUserInfoForWebtrekk(webtrekkData, userInfo?.isAuthenticated)
    generateCategoryHierarchyForWebtrekk(hierarchyData, webtrekkData)
    generateFiltersForWebtrekk(appliedFacetArray, webtrekkData, isClosedCategory)

    dispatch(analyticsActionCreators.sendWebtrekkEvent.trigger(webtrekkData))
  }

  const handleUtagOverrideDataListener = () => {
    window?.HbEventBus?.on(UTAG_DATA_OVERRIDE_EVENT_NAME, (data) => {
      window.utagData = {
        ...window.utagData,
        ...seoInfo?.utagData,
        ...data,
      }
    })
  }

  const handleGetSeoInformation = () => {
    window?.HbEventBus?.on(UTAG_DATA_EVENT_NAME, async(seoState) => {
      const body = {
        ...seoState,
        appliedFilterCount: seoState?.appliedFilters?.length,
      }
      const location = window?.location?.search
      const hasBrandId = seoState?.appliedFilters?.some(appliedFilter => appliedFilter?.key === FACET_QUERY_PARAM_NAMES.BRAND)

      if (hasBrandId || location?.search(LOCATION_SEARCH_REGEX) > 0 || isHurlyPageByWindow()) {
        setShowBannerState(false)
      } else {
        const seoCategoryId = seoState?.categoryInfo?.categoryId?.toString()
        setShowBannerState(true)

        if (newCategoryId.current !== seoCategoryId) {
          newCategoryId.current = seoCategoryId
          setIsBannerLoading(true)
          await fetchCMSComponents()
          setIsBannerLoading(false)
        }
      }

      sendWebtrekkData()
      handleLocationChange(prevProps)
      fetchSeo(body)
    })
  }

  const getLandingPageCmsComponent = () => {
    return cmsComponentsMap?.get(categoryLandingPageInfoRef?.current?.value)
  }

  const scriptRenderMethodClientSide = routeChange || !serverSideRendering
  const isAppliedPagination = queryParams[PAGINATION_PARAM_NAMES.SORT_BY] || (queryParams[PAGINATION_PARAM_NAMES.PAGE] && queryParams[PAGINATION_PARAM_NAMES.PAGE] !== '1')
  const displayDictionaryContent = pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_DICTIONARY_CONTENT_FOR_PWA_DESKTOP_PAGES) || false
  const isDictionaryDetailPage = displayDictionaryContent ? dictionaryDetail && queryParams[DICTIONARY_DETAIL_PARAMS] : false
  const showDictionaryDetail = isDictionaryDetailPage && !isAppliedFilter && !isAppliedPagination
  const isFirstPage = (queryParams[PAGINATION_PARAM_NAMES.PAGE] || 1) === 1
  const showDictionaryList = displayDictionaryContent &&
    dictionaryArray?.length > 0 &&
    !isCollectionPage &&
    !isAppliedFilter &&
    !appliedSelectedAnyDeliveryValue &&
    !queryParams[DICTIONARY_DETAIL_PARAMS] &&
    (!isAppliedPagination || (process.env.BROWSER && window && isFirstPage))
  const titleElement = showDictionaryDetail ? 'div' : 'h1'
  const landingPageCmsComponent = getLandingPageCmsComponent()
  const displayLandingPage = Boolean(landingPageCmsComponent)
  const cmsBanner = cmsComponentsMap?.get(newCategoryId?.current || categoryId)
  const hasCmsBanner = cmsBanner && !cmsBanner?.isEmpty
  const isBannerToggleActive = pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_BANNER_ENABLED_FOR_DESKTOP_CATEGORY_PAGE)
  const showRecoProducts = pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_RECO_PRODUCTS_FOR_CATEGORY_PAGE)
  const displayFeedback = pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_FEEDBACK_PWA_DESKTOP)
  const isVerticalFilterSticky = pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.ENABLE_DESKTOP_STICKY_VERTICAL_FILTER_FROM_PWA)
  const isBannerActive = isBannerToggleActive && showBannerState && hasCmsBanner
  const firstProductSKU = productSKUs?.split(',')?.[0]
  const {appliedFilters = []} = useMemo(() => MoriaComponentsHelper.getSeoState(moriaComponents) || {}, [moriaComponents])
  const appliedBrands = appliedFilters?.find(appliedFilter => appliedFilter.mordorId === 'Brand')
  const firstAppliedBrand = appliedBrands?.selectedValues?.length === 1 ? appliedBrands?.selectedValues?.[0]?.mordorValue : null
  const isSelectedAppliedFacet = appliedFilters?.filter(facet => facet.mordorId === FACET_IDS.PHONE_MODEL)?.length > 0
  const showPLPRedesignLayout = !displayLandingPage && pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.SHOW_PLP_REDESIGN_LAYOUT)
  const {boostedFacets = [], categoryInfo} = useMemo(() => MoriaComponentsHelper.getSeoState(moriaComponents) || {}, [moriaComponents])
  const boostedBrands = boostedFacets?.find(facet => facet?.name === 'Marka')?.values
  const showSimilarProductsWidget = checkSimilarProductsWidgetVisibilty({
    appliedFilters,
    isCollectionPage
  })

  const structuredData = useMemo(() => (
    <ProductListStructuredData
      canonicalUrl={seoInfo?.canonicalUrl}
      title={seoInfo?.h1}
      isIndexedPage={seoInfo?.index === 'index'}
      itemListElement={itemListElement}
      totalProductCount={totalProductCount}/>
  ), [])

  const lastItemDisabled = !((isCollectionPage && IPHONE_CATEGORY_ID === categoryId) || (ANDROID_CATEGORY_ID === categoryId && CATEGORY_PAGE_ANDROID_TARGET_BRANDS.includes(firstAppliedBrand) && isSelectedAppliedFacet)); // eskiden yazılan bir logic?? düzeltilmeli.

  return (
    <div className={cx(s.root, s[platform])} id='layout_container'>
      <ListingPreloadImages
        bannerFragment={cmsBanner}
        isBannerActive={isBannerActive}
        productImages={firstProductImages}/>

      <HbClientSideHelmet
        seoInfo={seoInfo} helmetClientChanged={setSeoInfo(seoInfo)}
        pageType='category'
        pageValue={match?.params?.categoryId}/>

      {
        isBannerActive && (
          <div className={s.bannerContainer}>
            <div className={s.bannerInner}>
              {!isBannerLoading && (
                <CmsComponents
                  componentInfo={cmsBanner} scriptsOptions={{
                    scriptRenderMethodClientSide
                  }}/>
              )}
            </div>
            {
              showRecoProducts && (
                <div className={s.recoProductBanner}>
                  {moriaComponents?.[MORIA_COMPONENTS.PRODUCTS_BANNER] && !isBannerLoading && (
                    <VoltranFragment
                      fragmentInfo={moriaComponents?.[MORIA_COMPONENTS.PRODUCTS_BANNER]}
                      renderMethodClientSide={scriptRenderMethodClientSide}/>
                  )}
                </div>
              )
            }
          </div>
        )
      }

      {
        showDictionaryDetail && (
          <DictionaryDetail
            categoryName={categoryName}
            keyword={dictionaryDetail}/>
        )
      }

      {
        !showPLPRedesignLayout && (
          <div
            className={cx(s.productListTitleContainer, 'product-title', 'voltran-product-list-container')}
            id='page_title'>
            <HbPageTitle
              className={s.pageTitle} title={seoInfo?.h1} size={PAGE_TITLE_SIZES.LARGE}
              TitleElement={titleElement}/>
            <div className={s.breadcrumbs}>
              {
                categoryPageBreadCrumbConfigArray?.length > 0 &&
                <HbBreadCrumbView
                  className={s.breadcrumb}
                  rootIconEnabled={false}
                  size={BREADCRUMB_SIZES.MEDIUM}
                  isSchemaStructuredActive
                  lastItemDisabled={lastItemDisabled}
                  forceRedirect
                  itemConfigs={categoryPageBreadCrumbConfigArray}/>
              }
              {
                !displayLandingPage && (
                  <div>
                    <VoltranFragment
                      fragmentInfo={moriaComponents?.[MORIA_COMPONENTS.SORTING_BOX]}
                      renderMethodClientSide={scriptRenderMethodClientSide}/>
                  </div>
                )
              }
            </div>
          </div>
        )
      }

      {
        displayLandingPage &&
        <CmsComponents
          componentInfo={landingPageCmsComponent}
          scriptsOptions={{
            scriptRenderMethodClientSide,
          }}/>
      }

      {
        !displayLandingPage && (
          <MoriaComponents
            {...props}
            isSticky={isVerticalFilterSticky}
            moriaComponents={moriaComponents}
            pageType={DAVINCI_PAGE_TYPES.CATEGORY}
            pageValue={match?.params?.categoryId}
            scriptRenderMethodClientSide={scriptRenderMethodClientSide}
            showPLPRedesignLayout={showPLPRedesignLayout}
            breadCrumbConfigArray={categoryPageBreadCrumbConfigArray}
            seoInfo={seoInfo}
            titleElement={titleElement}
            categorySeoInfo={categorySeoInfo}
            lastItemDisabled={lastItemDisabled}/>
        )
      }

      {
        pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_COMPARED_TOP_SELLER_PRODUCTS_CATEGORY) && !queryParams[PAGINATION_PARAM_NAMES.PAGE] && (
          <ComparedProductsWithHydrationOnDemand
            brand={firstAppliedBrand}
            className={s.comparedProductsWrapper}
            title={`2025 yılında en çok tercih edilen ${seoInfo?.h1}`}
            products={comparedProducts}
            dispatch={dispatch}
            categoryId={categoryId}
            pageType={DAVINCI_PAGE_TYPES.CATEGORY}
            isBot={isBot}
            sku={firstProductSKU}/>
        )
      }

      {showSimilarProductsWidget && (
        <SimilarBrandAndSubcategory
          title='Benzer Ürünler'
          similarLists={[boostedBrands]}
          hasBorder
        />
      )}

      {
        keywords?.length > 0 &&
        <SearchKeywordRowListView
          title={CATEGORY_SEARCH_KEYWORD_TITLE.POPULAR_KEYWORDS}
          pageTypeForDavinci={DAVINCI_PAGE_TYPES.CATEGORY}
          searchKeywordArray={keywords}/>
      }

      {
        !displayLandingPage && displayFeedback && (
          <Feedback
            {...props}
            className={s.feedback}
            customWrapperClassName={s.feedbackWrapper}
            customButtonContainerClassName={s.feedbackButtonContainer}
            pageTypeForDavinci={DAVINCI_PAGE_TYPES.CATEGORY}
            pageValueForDavinci={match?.params?.categoryId}/>
        )
      }

      {
        showDictionaryList && (
          <DictionaryList
            categoryId={match?.params?.categoryId}
            categoryName={categoryName}
            dictionary={dictionaryArray}/>
        )
      }

      {structuredData}
    </div>
  )
})

CategoryPage.fetchData = async(dispatch, context) => {
  let result
  let fragments = []

  const {
    pathname,
    queryParams,
    pathParams,
    isBot,
    cookies,
    url,
    rawCookies,
    isCollectionPage,
    friendlyUrl,
  } = context
  const {
    categoryId,
    categoryUrlKeyword,
    brandIds,
  } = pathParams

  const locationCookie = jsonParse(cookies?.location)

  const fetchOperationContext = {
    serverSideRendering: true,
    redirection: null,
    results: [],
    seoInfo: null,
    noCache: null,
    pageValue: ''
  }

  let moriaData = {
    redirection: null,
    seoState: null,
    pageName: '',
  }

  const getFeatureTogglesResponse = await resolveWithSettleContext(dispatch(
    featureToggleActionCreators.getDesktopCategoryPageFeatureToggles.trigger(
      DESKTOP_CATEGORY_PAGE_FEATURE_TOGGLES,
    ),
  ))

  const featureToggleMap = generateFeatureToggleMap(getFeatureTogglesResponse?.result?.data?.data)
  const isAppliedFilter = getIsAppliedFilterForServer(url)

  let getDictionaryDetailResponse
  if (getFeatureTogglesResponse.hasError) {
    fetchOperationContext.isError = getFeatureTogglesResponse.result
    return fetchOperationContext
  }

  if (getFeatureTogglesResponse?.result?.data?.data) {
    fetchOperationContext.featureToggles = getFeatureTogglesResponse.result?.data.data
  }

  if (!isBot && !featureToggleMap?.get(FEATURE_TOGGLE_NAMES.SERVER_SIDE_RENDER_FOR_PWA_DESKTOP_CATEGORY_PAGE)) {
    fetchOperationContext.serverSideRendering = false
  }

  if (isCategoryApiPaginationLimitExceeded(queryParams[PAGINATION_PARAM_NAMES.PAGE], featureToggleMap?.get(FEATURE_TOGGLE_NAMES.PAGE_LIMIT_IN_LISTING_PAGES))) {
    const url = removeQueryStringFromUrl(context?.pathname) + generateQueryString({
      ...queryParams,
      [PAGINATION_PARAM_NAMES.PAGE]: null,
    })
    const redirectionUrl = isCollectionPage ? `${appConfig.baseUrl}/${friendlyUrl}` : url

    fetchOperationContext.redirection = {
      type: HTTP_STATUS_CODES.MOVED_PERMANENTLY,
      url: redirectionUrl,
    }
    return fetchOperationContext
  } else if (isBot || featureToggleMap?.get(FEATURE_TOGGLE_NAMES.SERVER_TO_CLIENT_SEND_INITIAL_DATA_FOR_PWA_DESKTOP_CATEGORY_PAGE)) {
    let getCmsComponentResponse
    const hasLandingPage = LANDING_PAGES_CATEGORIES?.some(category => category === categoryId)
    if (featureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_PWA_DESKTOP_CATEGORY_PAGES_FROM_CMS) && hasLandingPage) {
      await resolveWithSettleContext(dispatch(
        landingPageActionCreators.getCategoryLandingPageInfo.trigger(
          categoryId,
          LANDING_PAGE_ENVIRONMENTS.DESKTOP,
        ),
      )).then(async getCategoryLandingPageResponse => {
        if (!getCategoryLandingPageResponse?.result?.data?.data) {
          return getCategoryLandingPageResponse
        }

        const categoryLandingPageInfo = findCategoryLandingPage(
          getCategoryLandingPageResponse?.result?.data?.data,
          categoryId,
          LANDING_PAGE_ENVIRONMENTS.DESKTOP,
        )

        if (categoryLandingPageInfo) {
          getCmsComponentResponse = await resolveWithSettleContext(dispatch(
            cmsComponentActionCreators.getCmsComponent.trigger([{
              name: categoryLandingPageInfo?.value,
              isBot: isBot
            }]),
          ))
        }

        return getCategoryLandingPageResponse
      })
    }

    if (featureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_DICTIONARY_CONTENT_FOR_PWA_DESKTOP_PAGES)) {
      await resolveWithSettleContext(
        dispatch(
          dictionaryActionCreators.getDictionary.trigger(
            categoryId,
          ),
        ),
      )
      if (queryParams && queryParams[DICTIONARY_DETAIL_PARAMS] && !isAppliedFilter) {
        getDictionaryDetailResponse = await resolveWithSettleContext(
          dispatch(
            dictionaryActionCreators.getDictionaryDetail.trigger(
              categoryId,
              queryParams[DICTIONARY_DETAIL_PARAMS],
            ),
          ),
        )
      }
    }

    if (!fetchOperationContext.redirection && getDictionaryDetailResponse?.result?.data) {
      fetchOperationContext.redirection = checkForDictionaryToCategoryRedirection(
        getDictionaryDetailResponse.result.data,
        categoryUrlKeyword,
        categoryId,
        queryParams,
      )
    }

    if (getFeatureTogglesResponse.hasError) {
      fetchOperationContext.isError = getFeatureTogglesResponse.result
      return fetchOperationContext
    }

    if (getFeatureTogglesResponse?.result?.data?.data) {
      fetchOperationContext.featureToggles = getFeatureTogglesResponse.result?.data.data
    }

    if (!isBot && !featureToggleMap?.get(FEATURE_TOGGLE_NAMES.SERVER_SIDE_RENDER_FOR_PWA_DESKTOP_CATEGORY_PAGE)) {
      fetchOperationContext.serverSideRendering = false
    }

    const {serverUrl: moriaUrl} = MoriaComponentsHelper.getMoriaRequestUrl({
      suffix: url,
      excludedPartials: [MORIA_COMPONENTS.SEARCH_RESULT_SUMMARY, MORIA_COMPONENTS.GOOGLE_VIDEO_ADX, MORIA_COMPONENTS.JET_DELIVERY]
    })
    const moriaCookies = getCookiesByPrefix({rawCookies, prefixes: MORIA_PASS_COOKIE_PREFIXES})
    const isVerticalFilterSticky = featureToggleMap?.get(FEATURE_TOGGLE_NAMES.ENABLE_DESKTOP_STICKY_VERTICAL_FILTER_FROM_PWA)
    const showBanner = !brandIds && shouldRequestBanner({locationUrl: context?.url})
    let brandTopPopulerFilterMordorValues = []
    let moriaProductListMetrics = null

    fragments.push({
      ...DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.MORIA_LISTING],
      url: moriaUrl,
      clientUrl: moriaUrl,
      paramater: {
        ...isVerticalFilterSticky ? {} : {stickyPartials: 'VerticalFilter'},
        [FACET_QUERY_PARAM_NAMES.CITY_CODE]: locationCookie?.city?.code,
        [FACET_QUERY_PARAM_NAMES.RECEIVER_CITY]: locationCookie?.city?.name,
        [FACET_QUERY_PARAM_NAMES.RECEIVER_TOWN]: locationCookie?.town?.name,
        [FACET_QUERY_PARAM_NAMES.RECEIVER_DISTRICT]: locationCookie?.district?.name,
        isBot
      },
      headers: {
        ...moriaCookies,
        collectionpage: isCollectionPage || false,
        friendlyurl: friendlyUrl || ''
      }
    })

    result = await resolvePromiseList(Promise.all([
      resolveWithSettleContext(
        dispatch(
          voltranActionCreators.getFragments.trigger(fragments),
        ),
      ),
      resolveWithSettleContext(
        dispatch(
          categoryActionCreators.getCategoryRedirections.trigger(
            {
              ...queryParams,
              categoryId,
            }
          ),
        ),
      ),
      showBanner && featureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_BANNER_ENABLED_FOR_DESKTOP_CATEGORY_PAGE) && resolveWithSettleContext(dispatch(
        cmsComponentActionCreators.getCmsComponent.trigger([{
          categoryId,
          ...DESKTOP_LISTING_BANNER_SETTINGS,
          isBot: isBot
        }]),
      )),
      ...getCmsComponentResponse
        ? [
          resolveWithSettleContext(
            dispatch(
              categoryActionCreators.getCategoryInformation.trigger(
                categoryId,
                {
                  isDynamic: true,
                },
              ),
            ),
          )]
        : [],
    ])).then(async([
      fragmentsResponse,
      categoryResponseData,
      cmsComponentResponse,
      categoryInformationData,
    ]) => {
      if (isExistPartialFragment(fragmentsResponse?.result)) {
        fetchOperationContext.isPartialContent = true
      }

      if (fragmentsResponse?.result?.[0].result?.response?.status === HTTP_STATUS_CODES.NOT_FOUND) {
        fetchOperationContext.isNotFound = true
        return fetchOperationContext
      }

      if (!fetchOperationContext?.redirection && getCmsComponentResponse) {
        fetchOperationContext.redirection = checkForCategoryRedirection(
          categoryInformationData?.result?.data,
          categoryUrlKeyword,
          categoryId,
          queryParams,
          brandIds,
          getCmsComponentResponse,
        )
      }

      const moriaProductData = fragmentsResponse?.result?.[0]?.result?.data
      const moriaHeaders = fragmentsResponse?.result?.[0]?.result?.headers
      const {
        redirection = null,
        seoState = null,
        pageData = null
      } = MoriaComponentsHelper.getProductListData(moriaProductData)
      brandTopPopulerFilterMordorValues = MoriaComponentsHelper.getBrandTopPopulerValues(moriaProductData)
      const {productListMetrics = null} = MoriaComponentsHelper.getPageData(moriaProductData)

      moriaProductListMetrics = productListMetrics

      if (isBot && featureToggleMap?.get(FEATURE_TOGGLE_NAMES.DISPLAY_COMPARED_TOP_SELLER_PRODUCTS_CATEGORY) && !queryParams[PAGINATION_PARAM_NAMES.PAGE]) {
        const productData = MoriaComponentsHelper.getPageData(moriaProductData)
        const firstProductSKU = productData?.productSKUs?.split(',')[0]
        const appliedBrands = seoState?.appliedFilters?.find(appliedFilter => appliedFilter.mordorId === 'Brand')
        const firstAppliedBrand = appliedBrands?.selectedValues?.length === 1 ? appliedBrands?.selectedValues?.[0]?.mordorValue : null

        await dispatch(
          productActionCreators.getComparedProducts.trigger({
            categoryId,
            sku: firstProductSKU,
            brand: firstAppliedBrand,
          })
        )
      }

      const categoryResponseRedirection = categoryResponseData?.result?.data?.redirection

      if (moriaHeaders?.[X_VARNISH_CACHE_CONTROL_KEY]) {
        fetchOperationContext.noCache = moriaHeaders?.[X_VARNISH_CACHE_CONTROL_KEY]
      }
      if (categoryResponseRedirection && categoryResponseRedirection?.url) {
        moriaData.redirection = categoryResponseRedirection
      } else if (moriaProductData && seoState?.categoryInfo) {
        moriaData.redirection = redirection
        moriaData.seoState = seoState

        if (moriaData?.seoState?.categoryInfo?.categoryUrlKeyword !== categoryUrlKeyword) {
          moriaData.redirection = {
            url: generateCategoryUrl(
              moriaData?.seoState?.categoryInfo?.categoryUrlKeyword,
              categoryId,
              {
                brandIds: brandIds,
                queryParams: queryParams,
              })
          }
        }
      } else {
        fetchOperationContext.isError = {message: 'error'}
      }

      fetchOperationContext.productList = pageData?.productSKUs?.split(FILTER_DELIMITER)

      return fetchOperationContext
    })

    const fullPath = friendlyUrl ? combineUrlAndQueryParams({
      url: friendlyUrl,
      paramsObject: queryParams
    }) : url

    if (Object.keys(queryParams)?.length <= 1 && queryParams[PAGINATION_PARAM_NAMES.PAGE]) {
      delete queryParams[PAGINATION_PARAM_NAMES.PAGE]
    }

    const isDictionaryDetailPage = Boolean(getDictionaryDetailResponse?.result?.data)
    const keyword = queryParams[DICTIONARY_DETAIL_PARAMS]

    const categoryHierarchy = moriaData?.seoState?.categoryInfo?.categoryHierarchy
    const parentCategory = moriaData?.seoState?.appliedFilters?.find(appliedFilter => appliedFilter?.key === 'AllCategories.CategoryId')
    const appliedBrands = moriaData?.seoState?.appliedFilters?.find(appliedFilter => appliedFilter?.mordorId === 'Brand')
    const parentCategoryName = parentCategory?.selectedValues?.[0]?.breadcrumbTitle

    await resolvePromiseList(Promise.all([
      resolveWithSettleContext(
        dispatch(getGoogleAdUnitListingPageActionCreators.getGoogleAdUnit.trigger(
          {categoryIdHierarchy: categoryHierarchy}
        )),
      ),
      featureToggleMap.get(FEATURE_TOGGLE_NAMES.DISPLAY_POPULAR_SEARCH_KEYWORDS_ON_CATEGORY_PAGE) && resolveWithSettleContext(
        dispatch(
          categoryActionCreators.getKeywords.trigger({
            categoryIdList: categoryHierarchy,
            brand: appliedBrands?.selectedValues?.length === 1 ? encodeURIComponent(appliedBrands?.selectedValues?.[0]?.mordorValue) : undefined,
          })
        )
      ),
      resolveWithSettleContext(
        dispatch(
          seoActionCreators.getGenericSeoInfo.trigger({
            pageType: isDictionaryDetailPage ? PAGE_TYPES.DICTIONARY : PAGE_TYPES.CATEGORY,
            pageValue: categoryId,
            absoluteUrl: removeQueryStringFromUrl(pathname) || '',
            currentPage: moriaData?.seoState?.currentPage,
            isMobile: false,
            url,
            keyword,
            filters: generateQueryString(queryParams),
            totalProductCount: moriaData?.seoState?.totalProductCount,
            appliedFilters: moriaData?.seoState?.appliedFilters,
            categoryInfo: moriaData?.seoState?.categoryInfo,
            friendlyUrl,
            isCollectionPage,
            topBrands: brandTopPopulerFilterMordorValues,
            discountedProductCount: moriaProductListMetrics?.discountedProductCount,
            maxDiscountedPercentage: moriaProductListMetrics?.maxDiscountedPercentage,
            minProductPrice: moriaProductListMetrics?.minProductPrice,
            averageProductPrice: moriaProductListMetrics?.averageProductPrice,
            fullPath,
          }),
        ),
      ),
      resolveWithSettleContext(
        dispatch(
          footerActionCreators.getFooterItems.trigger({
            categoryName: parentCategoryName,
            categoryHierarchy,
            platform: 'desktop'
          }
          )),
      ),
    ])).then(async([
      googleAdUnitResponse,
      popularKeywordsResponse,
      seoInfoResponse,
      footerItemsResponse
    ]) => {
      if (seoInfoResponse?.statusCode === HTTP_STATUS_CODES.GONE) {
        fetchOperationContext.isGone = true

        return fetchOperationContext
      }

      const adUnitPath = googleAdUnitResponse?.result?.data?.data

      if (adUnitPath) {
        const brandTarget = getBrand(moriaData?.seoState?.appliedFilters) || ''
        const categoryTarget = getCategory(moriaData?.seoState?.appliedFilters, moriaData?.seoState?.categoryInfo?.categoryId) || ''
        await resolveWithSettleContext(
          dispatch(
            voltranActionCreators.getFragments.trigger([{
              ...DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.ADVERTISEMENT],
              paramater: {
                adUnitPath,
                isPlaceHolder: 'true',
                targeting: `kategori:${categoryTarget}BrandFilter:${brandTarget}`,
                useBouncingIndicator: 'true',
                preventHidingAd: true,
              }
            }]),
          ),
        )
      }

      fetchOperationContext.seoInfo = seoInfoResponse?.isResolved && seoInfoResponse?.result?.data?.data
    })

    if (isDictionaryDetailPage) {
      const updateSeoData = {
        h1: moriaData?.seoState?.categoryInfo?.categorySeoName,
        ...!fetchOperationContext?.seoInfo?.title && {title: `${keyword} Nedir? - ${keyword} Ne Demek? - Hepsiburada`},
        ...!fetchOperationContext?.seoInfo?.description && {title: `${keyword} nedir, ${keyword} ne demek gibi soruların cevabını bulabileceğiniz, ${keyword} hakkında bilgilerin yer aldığı sözlük sayfası`}
      }
      fetchOperationContext.seoInfo = {...fetchOperationContext?.seoInfo, ...updateSeoData}
      dispatch(seoActionCreators.updateSeoInfo(updateSeoData))
    }

    if (moriaData?.redirection?.url) {
      const moriaRedirectionUrl = moriaData?.redirection?.url || '';
      const redirectPath = moriaRedirectionUrl?.startsWith('/') ? moriaRedirectionUrl : `/${moriaRedirectionUrl}`;
      const redirectionUrl = isCollectionPage ? `${appConfig.baseUrl}${redirectPath}` : moriaRedirectionUrl

      fetchOperationContext.redirection = {
        type: moriaData?.redirection?.statusCode || HTTP_STATUS_CODES.MOVED_PERMANENTLY,
        url: redirectionUrl,
      }
    }

    fetchOperationContext.categoryIdList = categoryHierarchy?.split('-')
    fetchOperationContext.pageValue = categoryId

    result = fetchOperationContext
    return result
  } else {
    return fetchOperationContext
  }
}

CategoryPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  pageFeatureToggleMap: ImmutablePropTypes.map,
  actions: PropTypes.object,
  location: PropTypes.object,
  userInfo: PropTypes.object,
  routeChange: PropTypes.bool,
  platform: PropTypes.string,
  isMobile: PropTypes.bool,
}

const mapStateToProps = (state) => {
  const {
    loadState: {
      serverSideRendering,
    },
    featureToggleState: {
      pageFeatureToggleMap,
    },
    voltranState: {
      fragmentsMap,
    },
    categoryState: {
      categories,
      isClosedCategory,
      categoryName,
      categorySeoInfo,
      subCategories,
      keywords
    },
    dictionaryState: {
      dictionaryArray,
      dictionaryDetail,
    },
    productState: {
      comparedProducts,
    },
    landingPageState: {
      categoryLandingPageInfo,
    },
    cmsComponentState: {
      cmsComponentsMap,
    },
    seoState: {
      seoInfo,
    },
    filterState: {
      appliedFacetArray,
    },
    userAgentState: {
      isBot,
      isCollectionPage,
      friendlyUrl,
    },
  } = state

  return {
    serverSideRendering,
    pageFeatureToggleMap,
    seoInfo,
    fragmentsMap,
    categories,
    categorySeoInfo,
    subCategories,
    appliedFacetArray,
    isClosedCategory,
    categoryName,
    dictionaryArray,
    dictionaryDetail,
    categoryLandingPageInfo,
    cmsComponentsMap,
    categoryPageBreadCrumbConfigArray: seoBreadCrumbConfigArraySelector(state),
    hasAppliedFilterOnlyOneAuthor: hasAppliedFilterOnlyOneAuthorSelector(state),
    isBot,
    keywords,
    isCollectionPage,
    friendlyUrl,
    comparedProducts,
  }
}

export default connect(mapStateToProps)(
  hbPageWithUserInfo(
    hbPageWithAnalytics(
      CategoryPage,
    ),
  ),
)
